import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

interface Experiment {
  id: string;
  name: string;
}

interface ExperimentCohort {
  cohort: number;
  success: boolean;
}

interface TesseractConfig {
  enabled: boolean;
  packet: { abCohorts: Record<string, ExperimentCohort> };
  userOverrides: Record<string, Record<string, number>>;
}

interface Props {
  dev: string;
  enqueueSnackbar: (msg: string, opts: { variant: string }) => void;
}

const emptyConfig = (): TesseractConfig => ({
  enabled: false,
  packet: { abCohorts: {} },
  userOverrides: {},
});

const TesseractFeatures: React.FC<Props> = ({ dev, enqueueSnackbar }) => {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [config, setConfig] = useState<TesseractConfig>(emptyConfig());
  const [newUserId, setNewUserId] = useState<Record<string, string>>({});
  const [newCohort, setNewCohort] = useState<Record<string, string>>({});

  useEffect(() => {
    axios
      .get(`/api/dev/${dev}/tesseract/experiments`)
      .then((res) => setExperiments(res.data || []))
      .catch((err) =>
        enqueueSnackbar("Failed to load experiments: " + err, { variant: "error" })
      );

    axios
      .get(`/api/dev/${dev}/consul/tesseractToolMockConfig`)
      .then((res) => {
        const data = res.data;
        setConfig({
          enabled: data.enabled ?? false,
          packet: { abCohorts: data.packet?.abCohorts ?? {} },
          userOverrides: data.userOverrides ?? {},
        });
      })
      .catch(() => {
        setConfig(emptyConfig());
      });
  }, [dev]);

  const saveConfig = (updated: TesseractConfig) => {
    axios
      .post(`/api/dev/${dev}/consul/tesseractToolMockConfig`, updated)
      .then(() => enqueueSnackbar("Saved successfully", { variant: "success" }))
      .catch((err) =>
        enqueueSnackbar("Save failed: " + err, { variant: "error" })
      );
  };

  const toggleGlobalEnabled = () => {
    const updated = { ...config, enabled: !config.enabled };
    setConfig(updated);
  };

  const toggleExperimentEnabled = (expId: string) => {
    const cohorts = { ...(config.packet?.abCohorts ?? {}) };
    if (cohorts[expId]) {
      delete cohorts[expId];
    } else {
      cohorts[expId] = { cohort: 0, success: true };
    }
    setConfig({ ...config, packet: { abCohorts: cohorts } });
  };

  const setCohortValue = (expId: string, value: string) => {
    const cohorts = { ...(config.packet?.abCohorts ?? {}) };
    cohorts[expId] = { cohort: parseInt(value) || 0, success: true };
    setConfig({ ...config, packet: { abCohorts: cohorts } });
  };

  const addUserOverride = (expId: string) => {
    const userId = (newUserId[expId] || "").trim();
    const cohort = parseInt(newCohort[expId] || "0");
    if (!userId) {
      enqueueSnackbar("UserID cannot be empty", { variant: "error" });
      return;
    }
    const overrides = JSON.parse(JSON.stringify(config.userOverrides ?? {}));
    if (!overrides[userId]) overrides[userId] = {};
    overrides[userId][expId] = cohort;
    const updated = { ...config, userOverrides: overrides };
    setConfig(updated);
    setNewUserId({ ...newUserId, [expId]: "" });
    setNewCohort({ ...newCohort, [expId]: "" });
    saveConfig(updated);
  };

  const removeUserOverride = (userId: string, expId: string) => {
    const overrides = JSON.parse(JSON.stringify(config.userOverrides ?? {}));
    if (overrides[userId]) {
      delete overrides[userId][expId];
      if (Object.keys(overrides[userId]).length === 0) {
        delete overrides[userId];
      }
    }
    const updated = { ...config, userOverrides: overrides };
    setConfig(updated);
    saveConfig(updated);
  };

  const getUserOverridesForExp = (expId: string): { userId: string; cohort: number }[] => {
    const result: { userId: string; cohort: number }[] = [];
    for (const [userId, expMap] of Object.entries(config.userOverrides ?? {})) {
      if (expId in expMap) {
        result.push({ userId, cohort: expMap[expId] });
      }
    }
    return result;
  };

  const isExpEnabled = (expId: string) => !!config.packet?.abCohorts?.[expId];

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="h5">Tesseract Features</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={config.enabled}
              onChange={toggleGlobalEnabled}
              color="primary"
            />
          }
          label="Global Mock Enabled"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => saveConfig(config)}
        >
          Save All
        </Button>
      </Box>

      {experiments.map((exp) => {
        const enabled = isExpEnabled(exp.id);
        const overrides = getUserOverridesForExp(exp.id);

        return (
          <Accordion key={exp.id} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                <Typography sx={{ fontWeight: 600 }}>{exp.name}</Typography>
                <Chip label={`ID: ${exp.id}`} size="small" variant="outlined" />
                <Chip
                  label={enabled ? "Enabled" : "Disabled"}
                  size="small"
                  color={enabled ? "success" : "default"}
                />
                {overrides.length > 0 && (
                  <Chip
                    label={`${overrides.length} user override${overrides.length > 1 ? "s" : ""}`}
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Global Cohort
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={() => toggleExperimentEnabled(exp.id)}
                        color="primary"
                      />
                    }
                    label="Override enabled"
                  />
                  {enabled && (
                    <TextField
                      label="Cohort value"
                      type="number"
                      size="small"
                      value={config.packet?.abCohorts?.[exp.id]?.cohort ?? 0}
                      onChange={(e) => setCohortValue(exp.id, e.target.value)}
                      sx={{ width: 140 }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Per-User Overrides
                </Typography>

                {overrides.length > 0 && (
                  <Table size="small" sx={{ mb: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>UserID</TableCell>
                        <TableCell>Cohort</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overrides.map(({ userId, cohort }) => (
                        <TableRow key={userId}>
                          <TableCell>{userId}</TableCell>
                          <TableCell>{cohort}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => removeUserOverride(userId, exp.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label="UserID"
                    size="small"
                    value={newUserId[exp.id] ?? ""}
                    onChange={(e) =>
                      setNewUserId({ ...newUserId, [exp.id]: e.target.value })
                    }
                    sx={{ width: 200 }}
                  />
                  <TextField
                    label="Cohort"
                    type="number"
                    size="small"
                    value={newCohort[exp.id] ?? ""}
                    onChange={(e) =>
                      setNewCohort({ ...newCohort, [exp.id]: e.target.value })
                    }
                    sx={{ width: 100 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => addUserOverride(exp.id)}
                  >
                    Add Override
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TesseractFeatures;
